import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, switchMap } from 'rxjs';
import { oeeContext } from '../../../core/interceptors/api-interceptor.interceptor';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IDropdownSettings } from '../mat-dropdown/scw-mat-dropdown.interface';
import { IShift } from './shift-selection.interface';

export interface IFormAssociationComponentState {
  shifts: IShift[];
  shiftsLoading: ELoadStatus;
}

@Injectable()
export class ShiftSelectionStore extends ComponentStore<IFormAssociationComponentState> {
  readonly shiftsDropdown$ = this.select((state: IFormAssociationComponentState) =>
    state.shifts.map((s: IShift) => ({ id: s.id, name: s.name })),
  );

  readonly loadShift = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ shiftsLoading: ELoadStatus.loading });

        return this.getShift(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                shifts: response.data,
                shiftsLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading shift data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    private readonly translate: TranslateService,
  ) {
    super({
      shifts: [],
      shiftsLoading: ELoadStatus.initial,
    });
  }

  private getShift(params: HttpParams): Observable<IGetManyResponse<IShift>> {
    return this.http.get<IGetManyResponse<IShift>>('scheduler/shifts', { context: oeeContext, params });
  }

  public getDefaultMultiSelectConfig(config: Partial<IDropdownSettings> & { text: string }): IDropdownSettings {
    return {
      enableSearchFilter: config.enableSearchFilter ?? true,
      hasDelayedDefaultValue: config.hasDelayedDefaultValue ?? true,
      ...(config.hint ? { hint: this.translate.instant(`widget.formIo.${config.hint}`) } : {}),
      isClientSide: config.isClientSide ?? true,
      repositionDropdownList: config.repositionDropdownList ?? true,
      singleSelection: config.singleSelection ?? false,
      text: this.translate.instant(`page.widgetContent.addEditDialog.${config.text}`),
    };
  }
}
